/**
 * Edição e exclusão de SÉRIE (recorrência ou parcelamento) na web com as mesmas regras do form
 * do app (app/transactions/[id].tsx): escolher o escopo — Apenas esta / Esta e futuras / Todas —,
 * esconder o escopo que arrastaria parcela pra dentro de fatura já paga (ADR 0086), confirmar
 * "Todas" quando reescreve dinheiro já concluído, editar a regra direto quando a ocorrência ainda
 * é só prevista, e pular/encerrar/apagar a série ao excluir. Tudo pelos repositórios do app.
 */
import { deleteTransaction, getSeriesOccurrenceStats, getTransactionById, updateRecurringSeriesByRule, updateTransaction } from '@/db/repositories/transactionRepository';
import { getSettledInvoiceInstallmentNumbers } from '@/db/repositories/installmentRepository';
import { materializeOccurrence, skipOccurrence } from '@/db/repositories/recurringRepository';
import { buildProjectedRecurringTransactionId } from '@/lib/virtualTransactionId';
import { needsAllScopeConfirmation } from '@/lib/seriesEditScope';
import type { EditScope } from '@/lib/seriesRebase';
import type { TransactionRow } from '../viewer/lib/types';
import { askChoice, askConfirm } from './confirm';

export type { EditScope };
type UpdateData = Parameters<typeof updateTransaction>[1];

export const isSeries = (t: TransactionRow) => !!(t.recurringId || t.installmentNumber);

/** O que a edição muda, pro cálculo de regras (fatura paga, "Todas" mexe em dinheiro concluído). */
export interface SeriesChange {
  date?: string;
  amount?: number;
  accountId?: string | null;
  creditCardId?: string | null;
  isConfirmed?: boolean;
}

/**
 * Pergunta o escopo da edição de uma linha de série. Devolve null se a pessoa cancelou.
 * Linha fora de série → 'single' sem perguntar.
 */
export async function chooseEditScope(t: TransactionRow, change: SeriesChange): Promise<EditScope | null> {
  if (!isSeries(t)) return 'single';
  const full = t.isProjected ? null : await getTransactionById(t.id);
  const installmentId = full?.installmentId ?? null;
  const onCard = !!(full?.creditCardId ?? t.creditCardId);
  const leavingCard = !!installmentId && onCard && change.creditCardId === null;
  // Mudar a data de parcela no cartão remaneja a série de fatura em fatura: escopo que tocaria
  // fatura já paga não é oferecido (o repositório recusaria depois — pior).
  const rebases = !!installmentId && onCard && !!change.date && change.date !== t.date;
  const anchor = t.installmentNumber ?? 1;
  const settled = rebases && installmentId ? await getSettledInvoiceInstallmentNumbers(installmentId) : [];
  const allHitsPaid = rebases && settled.some((n) => n !== anchor);
  const futureHitsPaid = rebases && settled.some((n) => n > anchor);

  const options = [
    { id: 'single', label: 'Apenas esta', description: leavingCard ? 'Só esta parcela sai da fatura e vai para a conta.' : 'Altera só este lançamento.' },
    ...(futureHitsPaid ? [] : [{ id: 'future', label: 'Esta e futuras', description: leavingCard ? 'Esta e as próximas saem das faturas e vão para a conta.' : 'Mantém o histórico e atualiza daqui para frente.' }]),
    ...(allHitsPaid ? [] : [{ id: 'all', label: 'Todas', description: leavingCard ? 'A compra inteira sai do cartão e as faturas são recalculadas.' : 'Reaplica a mudança em toda a série.' }]),
  ];
  const picked = await askChoice({
    title: t.installmentNumber ? 'Editar parcela' : 'Editar recorrência',
    text: leavingCard
      ? 'Você está tirando parcelas do cartão. Elas viram despesas na conta escolhida; as futuras ficam pendentes até a data de cada uma.'
      : allHitsPaid
        ? 'Esta parcela se repete. Mudar a fatura da série inteira mexeria numa fatura que você já pagou, então esse escopo não aparece aqui.'
        : 'Esta transação se repete. O que deseja editar?',
    options,
  });
  if (!picked) return null;
  if (picked !== 'all') return picked as EditScope;

  // "Todas" reescrevendo dinheiro já concluído: confirma com os números, como o app.
  const touchesSettledMoney =
    (change.amount !== undefined && Math.abs(change.amount - t.amount) >= 0.005)
    || (change.date !== undefined && change.date !== t.date)
    || (change.accountId !== undefined && (change.accountId ?? null) !== (t.accountId ?? null))
    || (change.creditCardId !== undefined && (change.creditCardId ?? null) !== (t.creditCardId ?? null))
    || (!onCard && change.isConfirmed !== undefined && change.isConfirmed !== !!t.isConfirmed);
  const stats = touchesSettledMoney ? await getSeriesOccurrenceStats({ recurringId: t.recurringId ?? null, installmentId }) : null;
  if (!needsAllScopeConfirmation(stats)) return 'all';
  const ok = await askConfirm({
    title: 'Alterar toda a série?',
    text: stats.total === 1
      ? 'Isso reescreve o lançamento já registrado desta série, que já está concluído, e todos os futuros. Para preservar o que já foi concluído, use "Esta e futuras".'
      : `Isso reescreve os ${stats.total} lançamentos já registrados desta série (já concluídos: ${stats.settled}) e todos os futuros. Para preservar o que já foi concluído, use "Esta e futuras".`,
    confirmLabel: 'Alterar todas',
    danger: true,
  });
  return ok ? 'all' : null;
}

/** Aplica a edição no escopo escolhido. Ocorrência prevista: "Todas" edita a regra; o resto concretiza antes. */
export async function applySeriesUpdate(t: TransactionRow, data: UpdateData, mode: EditScope): Promise<boolean> {
  if (t.isProjected && t.recurringId) {
    if (mode === 'all') { await updateRecurringSeriesByRule(t.recurringId, t.recurringOccurrenceDate ?? t.date, data); return true; }
    const id = await materializeOccurrence(t.recurringId, t.recurringOccurrenceDate ?? t.date);
    if (!id) return false;
    await updateTransaction(id, data, mode);
    return true;
  }
  await updateTransaction(t.id, data, mode);
  return true;
}

/** Pergunta o escopo da exclusão de uma linha de série. Null = cancelou. Fora de série → 'single'. */
export async function chooseDeleteScope(t: TransactionRow): Promise<EditScope | null> {
  if (!isSeries(t)) return 'single';
  const projected = !!t.isProjected;
  const picked = await askChoice({
    title: projected ? 'Cancelar cobrança' : 'Excluir transação',
    text: projected ? 'Esta cobrança ainda não aconteceu. O que você deseja cancelar?' : 'Esta transação se repete. O que você deseja excluir?',
    options: [
      { id: 'single', label: 'Apenas esta', description: projected ? 'Pula só esta cobrança; a série continua.' : 'Remove só este lançamento.', danger: true },
      { id: 'future', label: projected ? 'Desta data em diante' : 'Esta e as próximas', description: 'Apaga esta ocorrência e as seguintes.', danger: true },
      { id: 'all', label: 'Todas', description: 'Exclui toda a série.', danger: true },
    ],
  });
  return (picked as EditScope | null) ?? null;
}

/** Devolve o id real apagado quando dá pra desfazer (só "apenas esta" numa linha real). */
export async function applySeriesDelete(t: TransactionRow, mode: EditScope): Promise<string | null> {
  if (t.isProjected && t.recurringId) {
    const slot = t.recurringOccurrenceDate ?? t.date;
    if (mode === 'single') { await skipOccurrence(t.recurringId, slot); return null; }
    await deleteTransaction(buildProjectedRecurringTransactionId(t.recurringId, slot), mode);
    return null;
  }
  await deleteTransaction(t.id, mode);
  return mode === 'single' ? t.id : null;
}

